/* eslint-disable no-unused-vars, array-bracket-newline */
import { useCallback } from 'react';
import DesktopTable from '../../../molecules/Table/DesktopTable';
import Text from '../../../atoms/Text';
import Stats from '../Stats';
import { ReactComponent as RobotIcon } from '../images/robot.svg';
import { formatTimestamp, toFixed4 } from '../formatters';
import { formatAddress } from '../../../helpers/formatters';
import { useTableData } from './useTableData';
import { ITradesSelector } from '.';

export const LS_TRADES_HIDDEN_COLUMNS_KEY =
  'kattana-simple.trades_table_columns_hidden';
export const LS_TRADES_KEY = 'kattana-simple.trades_table_columns';

interface ITradesTableProps extends ITradesSelector {
  loading: boolean;
  currentWallet?: `0x${string}`;
  openTx: (tx: string) => void;
  openWallet: (wallet: string) => void;
  width?: number;
}

const sellClassName = 'row--Sell text--red-600';
const buyClassName = 'row--Buy text--green-600';
const myTrade = 'row--Active';

/**
 * Implements a table with trades
 * @param param0
 */
const TradesTable = ({
  openTx,
  openWallet,
  currentWallet,
  trades,
  base,
  quote,
  loading,
  flipped,
  address,
  quote_decimals,
  width,
  chain,
}: ITradesTableProps) => {
  const notUSDPrice = !quote.includes('USD');

  const { columns, data, tableColumnsWidth } = useTableData({
    trades,
    quote,
  });

  const headers = ['Date;Action;Price ($);Price;Value;Wallet']; // Headers
  const csvData = [
    ...headers,
    ...trades.map(
      (trade: any) =>
        `${trade.date};${trade.side};${toFixed4(trade.priceUSD)};${toFixed4(
          trade.price
        )};${toFixed4(trade.txValue)};${trade.wallet}`
    ),
  ];

  const renderCell = (cell: any) => {
    switch (cell.column.id) {
    case 'date':
      return <Text size={12}>{formatTimestamp(cell.value)}</Text>;
    case 'numericIn':
      return (
        <Text size={12}>
          {toFixed4(cell.value)} {base}
        </Text>
      );
    case 'numericOut':
      return (
        <Text size={12}>
          {toFixed4(cell.value)} {quote}
        </Text>
      );
    case 'price':
      return (
        <Text size={12} className={notUSDPrice ? '' : 'price'}>
          ({toFixed4(cell.value)})
        </Text>
      );
    case 'priceUSD':
      return (
        <Text size={12} className={'price'}>
          {toFixed4(cell.value)}
        </Text>
      );
    case 'txValue':
      return (
        <Text size={12}>
          {toFixed4(cell.value)}&nbsp;
          {cell.row.original.sandwich && (
            <RobotIcon
              data-effect='solid'
              data-tip={`Revenue : ${Math.abs(
                cell.row.original.sandwichAmount
              ).toFixed(5)} ${quote}`}
            />
          )}
        </Text>
      );
    case 'statistics':
      return (
        <Stats
          data={cell.row.original}
          quote_decimals={quote_decimals}
          quote={quote}
          address={address}
          chain={chain}
        />
      );
    case 'wallet':
      return (
        <div className='tx' onClick={() => openWallet(cell.value)}>
          <Text size={12}>{formatAddress(cell.value)}</Text>
        </div>
      );
    case 'txString':
      return (
        <div className='tx' onClick={() => openTx(cell.row.original.tx)}>
          <Text size={12}>{cell.value}</Text>
        </div>
      );
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  /**
   * acknowledge flipped when displaying buy/sell
   */
  const getClassNameForRow = useCallback(
    (row: any) => {
      const classNames: string[] = [];
      if (row.wallet === currentWallet) {
        classNames.push(myTrade);
      }
      if (row.side === 'Buy') {
        if (flipped) {
          classNames.push(sellClassName);
        } else {
          classNames.push(buyClassName);
        }
      } else {
        if (flipped) {
          classNames.push(buyClassName);
        } else {
          classNames.push(sellClassName);
        }
      }
      return classNames.join(' ');
    },
    [flipped, currentWallet]
  );

  return (
    <DesktopTable
      hiddenColumnsKey={LS_TRADES_HIDDEN_COLUMNS_KEY}
      dataKey={LS_TRADES_KEY}
      title='trades'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      defaultSortedColumn={{ id: 'date', desc: true }}
      data={data}
      csvData={csvData}
      loading={loading}
      hiddenColumns={['blocknumber', 'txString']}
      getClassNameForRow={getClassNameForRow}
      renderCell={renderCell}
      hoveredKey='wallet'
    />
  );
};

export default TradesTable;
